import moment from "moment";
import type {
  AttributeGroupSummary,
  AttributeSchema,
  AttributeValue,
  CollectionSchema,
  CollectionValueRowSchema,
  PIMProduct,
} from "../../../../../types/types.PIM";
import type { AddressValue } from "../../../../admin/SellerAdmin/PIM/SellerAdminPIMAttributes/AddressAttributes/address.utils";
import { getAddressDisplayLines } from "../../../../admin/SellerAdmin/PIM/SellerAdminPIMAttributes/AddressAttributes/addressDisplay.utils";

const HTML_TAG_PATTERN = /<\/?[a-z][\s\S]*?>/i;

function looksLikeHtml(value: string): boolean {
  return HTML_TAG_PATTERN.test(value);
}

/**
 * Convert Quill/HTML rich text to a single-line plain string for CSV/compare.
 * Uses the DOM when available so entities (&nbsp;, &amp;) and nested tags
 * are handled; falls back to a regex strip in non-browser environments.
 */
export function stripHtmlTags(html: string): string {
  if (!html) return "";
  // Preserve word boundaries between block-level tags (e.g. </p><p>).
  const withBreaks = html
    .replace(/<(br|hr)\s*\/?>/gi, " ")
    .replace(/<\/(p|div|h[1-6]|li|tr|blockquote|pre)>/gi, " ");
  let text = withBreaks;
  if (typeof DOMParser !== "undefined") {
    const doc = new DOMParser().parseFromString(withBreaks, "text/html");
    text = doc.body.textContent || "";
  } else {
    text = withBreaks.replace(/<[^>]*>/g, " ");
  }
  return text
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export const MISSING_VALUE_DISPLAY = "--";
export const MIN_COMPARE_PRODUCTS = 2;
export const MAX_COMPARE_PRODUCTS = 4;

export function isAttributeNotApplicable(
  attribute: { is_not_applicable?: boolean } | undefined
): boolean {
  return Boolean(attribute?.is_not_applicable);
}

/** Role-view visibility. Missing flag is treated as visible (API default is true). */
export function isAttributeVisible(
  attribute: { is_visible?: boolean } | undefined
): boolean {
  return attribute?.is_visible !== false;
}

export function isCollectionVisible(
  collection: { is_visible?: boolean } | undefined
): boolean {
  return collection?.is_visible !== false;
}

function isAttributeShownInCompare<
  T extends { is_not_applicable?: boolean; is_visible?: boolean }
>(attribute: T | undefined): attribute is T {
  return (
    attribute != null &&
    !isAttributeNotApplicable(attribute) &&
    isAttributeVisible(attribute)
  );
}

export const SESSION_COMPARE_META_KEY = "compareProductMeta";
export const SESSION_SELECTED_ROWS_KEY = "selectedRows";

export type CompareProductMeta = {
  product_number: string;
  uuid: string;
  template_id: string | null;
  name: string;
};

export type CompareSectionKind = "group" | "collection";

export type CompareSection = {
  id: string;
  name: string;
  display_name: string;
  kind: CompareSectionKind;
  /** Collection Key Attribute name — required for collection sections (Part 2). */
  keyAttributeName?: string;
};

/** Template Settings pairing used to drive which sections appear and how rows align. */
export type CompareConfigItem = {
  target_type: CompareSectionKind;
  target_id: string;
  target_name: string;
  target_display_name: string;
  key_attribute_name: string | null;
};

const EXCLUDED_COLLECTION_NAMES = new Set(["assets"]);

export function getCompareDisableReason(
  selectedCount: number,
  templateIds: (string | null | undefined)[],
  t: (key: string, options?: Record<string, unknown>) => string,
  options?: {
    entityLabelPlural?: string;
    /** false = template has no pairings; null/undefined = unknown / still loading */
    hasComparisonConfig?: boolean | null;
  }
): string | null {
  const entityLabel = options?.entityLabelPlural ?? "Products";
  if (selectedCount > MAX_COMPARE_PRODUCTS) {
    return t("You can compare a maximum of 4 {{entity}}", {
      entity: entityLabel,
    });
  }
  if (selectedCount < MIN_COMPARE_PRODUCTS) {
    return t("Select at least 2 {{entity}} to compare", {
      entity: entityLabel.toLowerCase(),
    });
  }
  const knownTemplateIds = templateIds.filter(
    (id): id is string => typeof id === "string" && id.length > 0
  );
  // If we know templates for some/all selections, they must match.
  // Unknown templates (e.g. select-all before meta is hydrated) are validated
  // on the compare page after full product fetch.
  if (knownTemplateIds.length > 0 && new Set(knownTemplateIds).size > 1) {
    return t("{{entity}} must be of the same template to compare", {
      entity: entityLabel,
    });
  }
  if (options?.hasComparisonConfig === false) {
    return t(
      "No groups or collections are configured for comparison on this template"
    );
  }
  return null;
}

/**
 * Build ordered compare sections from the template's Profile Comparison
 * configuration (Part 2). Only configured groups/collections appear.
 * Collections also require a Key Attribute visible to the user and at least
 * one compared profile with rows.
 */
export function getCompareSections(
  product: PIMProduct,
  allProducts: PIMProduct[] = [product],
  configs: CompareConfigItem[] = []
): CompareSection[] {
  if (!configs.length) return [];

  const comparedProducts = allProducts.length ? allProducts : [product];
  const sections: CompareSection[] = [];

  for (const config of configs) {
    if (config.target_type === "group") {
      const group =
        product.product_schema.groups.find(
          (g) =>
            g.id === config.target_id ||
            g.name.toLowerCase() === config.target_name.toLowerCase()
        ) ?? findGroupByName(product, config.target_name);
      if (!group?.attributes?.length) continue;
      if (
        getAlignedGroupAttributes(comparedProducts, group.name).length === 0
      ) {
        continue;
      }
      sections.push({
        id: group.id,
        name: group.name,
        display_name: group.display_name || group.name,
        kind: "group",
      });
      continue;
    }

    const collection =
      product.product_schema.collections.find(
        (c) =>
          c.id === config.target_id ||
          c.name.toLowerCase() === config.target_name.toLowerCase()
      ) ?? findCollectionByName(product, config.target_name);
    if (
      !collection?.columns?.length ||
      EXCLUDED_COLLECTION_NAMES.has(collection.name.toLowerCase())
    ) {
      continue;
    }
    const keyAttributeName = config.key_attribute_name;
    if (!keyAttributeName) continue;

    // Hide the collection when the Key Attribute is not visible via Views Management.
    if (
      !isKeyAttributeVisibleOnAnyProduct(
        comparedProducts,
        collection.name,
        keyAttributeName
      )
    ) {
      continue;
    }

    const hasRows = comparedProducts.some((p) => {
      const col = findCollectionByName(p, collection.name);
      return (col?.value_rows?.length ?? 0) > 0;
    });
    if (!hasRows) continue;

    if (
      getAlignedCollectionColumns(comparedProducts, collection.name).length ===
      0
    ) {
      continue;
    }

    sections.push({
      id: collection.id,
      name: collection.name,
      display_name: collection.display_name || collection.name,
      kind: "collection",
      keyAttributeName,
    });
  }

  return sections;
}

function isKeyAttributeVisibleOnAnyProduct(
  products: PIMProduct[],
  collectionName: string,
  keyAttributeName: string
): boolean {
  return products.some((product) => {
    const collection = findCollectionByName(product, collectionName);
    if (!isCollectionVisible(collection)) return false;
    const column = collection?.columns.find(
      (c) => c.name.toLowerCase() === keyAttributeName.toLowerCase()
    );
    return isAttributeShownInCompare(column);
  });
}

export function findGroupByName(
  product: PIMProduct,
  name: string
): AttributeGroupSummary | undefined {
  return product.product_schema.groups.find(
    (g) => g.name.toLowerCase() === name.toLowerCase()
  );
}

export function findCollectionByName(
  product: PIMProduct,
  name: string
): CollectionSchema | undefined {
  return product.product_schema.collections.find(
    (c) => c.name.toLowerCase() === name.toLowerCase()
  );
}

export function findAttributeByName(
  group: AttributeGroupSummary | undefined,
  attributeName: string
): AttributeSchema | undefined {
  return group?.attributes.find(
    (a) => a.name.toLowerCase() === attributeName.toLowerCase()
  );
}

/** Union of attribute definitions across products for a named group (aligned by attribute name). */
export function getAlignedGroupAttributes(
  products: PIMProduct[],
  groupName: string
): AttributeSchema[] {
  const byName = new Map<string, AttributeSchema>();
  for (const product of products) {
    const group = findGroupByName(product, groupName);
    for (const attr of group?.attributes ?? []) {
      if (!byName.has(attr.name.toLowerCase())) {
        byName.set(attr.name.toLowerCase(), attr);
      }
    }
  }
  // Preserve order from the first product that has the group
  const firstGroup = products
    .map((p) => findGroupByName(p, groupName))
    .find(Boolean);
  if (!firstGroup) return Array.from(byName.values());

  const ordered: AttributeSchema[] = [];
  const used = new Set<string>();
  for (const attr of firstGroup.attributes) {
    const key = attr.name.toLowerCase();
    const def = byName.get(key);
    if (def) {
      ordered.push(def);
      used.add(key);
    }
  }
  for (const [key, attr] of byName) {
    if (!used.has(key)) ordered.push(attr);
  }
  // Omit rows that are N/A or not visible on every compared product.
  return ordered.filter((attrDef) =>
    products.some((product) =>
      isAttributeShownInCompare(
        getProductGroupAttribute(product, groupName, attrDef.name)
      )
    )
  );
}

/** Resolve the attribute instance for a product in a named group (by attribute name). */
export function getProductGroupAttribute(
  product: PIMProduct,
  groupName: string,
  attributeName: string
): AttributeSchema | undefined {
  return findAttributeByName(
    findGroupByName(product, groupName),
    attributeName
  );
}

/**
 * Build comparison rows for a group: one row per attribute, values aligned to products[].
 * Values are taken from each product independently so columns cannot share state.
 */
export function getGroupCompareRows(
  products: PIMProduct[],
  groupName: string
): Array<{
  attributeName: string;
  displayName: string;
  attributesByProductId: Record<string, AttributeSchema | undefined>;
}> {
  const aligned = getAlignedGroupAttributes(products, groupName);
  return aligned.map((attrDef) => {
    const attributesByProductId: Record<string, AttributeSchema | undefined> =
      {};
    for (const product of products) {
      // Clone so React/display components cannot accidentally share references
      // across columns (attribute ids are identical across same-template products).
      const source = getProductGroupAttribute(product, groupName, attrDef.name);
      attributesByProductId[product.id] = isAttributeShownInCompare(source)
        ? {
            ...source,
            values: source.values ? source.values.map((v) => ({ ...v })) : [],
          }
        : undefined;
    }
    return {
      attributeName: attrDef.name,
      displayName: attrDef.display_name || attrDef.name,
      attributesByProductId,
    };
  });
}

export function attributeValueToPlainText(
  attribute: AttributeSchema | undefined
): string {
  if (
    !attribute ||
    attribute.is_not_applicable ||
    attribute.is_visible === false
  ) {
    return MISSING_VALUE_DISPLAY;
  }
  const values = attribute.values ?? [];
  if (!values.length) {
    return MISSING_VALUE_DISPLAY;
  }

  switch (attribute.input_type) {
    case "date": {
      const raw = values[0]?.value;
      if (raw == null || raw === " " || raw === "") {
        return MISSING_VALUE_DISPLAY;
      }
      return moment(raw as string).format("MMM DD, YYYY");
    }
    case "toggle": {
      const raw = values[0]?.value;
      if (raw == null) return MISSING_VALUE_DISPLAY;
      return raw ? "On" : "Off";
    }
    case "multi_select":
      return (
        values
          .map((v) => v.enum_variant?.variant ?? String(v.value ?? ""))
          .filter(Boolean)
          .join("; ") || MISSING_VALUE_DISPLAY
      );
    case "multiline_entry": {
      const raw = values[0]?.value;
      if (raw == null || raw === "" || raw === " ") {
        return MISSING_VALUE_DISPLAY;
      }
      const text = stripHtmlTags(String(raw));
      return text || MISSING_VALUE_DISPLAY;
    }
    case "form_field": {
      const raw = values[0]?.value;
      if (raw == null || raw === "" || raw === " ") {
        return MISSING_VALUE_DISPLAY;
      }
      const asString = String(raw);
      const text = looksLikeHtml(asString) ? stripHtmlTags(asString) : asString;
      return text.trim() || MISSING_VALUE_DISPLAY;
    }
    case "product_reference": {
      return (
        values
          .map((v) => formatReferenceName(v))
          .filter(Boolean)
          .join("; ") || MISSING_VALUE_DISPLAY
      );
    }
    case "object_reference": {
      return (
        values
          .map((v) => formatReferenceName(v))
          .filter(Boolean)
          .join("; ") || MISSING_VALUE_DISPLAY
      );
    }
    case "user": {
      return (
        values
          .map((v) => formatUserValue(v))
          .filter(Boolean)
          .join("; ") || MISSING_VALUE_DISPLAY
      );
    }
    case "image":
      return values.length ? `${values.length} file(s)` : MISSING_VALUE_DISPLAY;
    case "document":
      return (
        values.map(getDocumentAssetId).filter(Boolean).join("; ") ||
        MISSING_VALUE_DISPLAY
      );
    case "address": {
      const raw = values[0]?.value;
      if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
        return MISSING_VALUE_DISPLAY;
      }
      // Match AttributeValueDisplay: postal lines + labeled meta fields.
      const { postalLines, metaFields } = getAddressDisplayLines(
        raw as AddressValue,
        (key) => key
      );
      const parts = [
        ...postalLines,
        ...metaFields.map((field) => `${field.label}: ${field.value}`),
      ];
      return parts.length ? parts.join("; ") : MISSING_VALUE_DISPLAY;
    }
    default: {
      if (values.length > 1) {
        return (
          values
            .map((v) => stringifyPrimitiveValue(v))
            .filter((s) => s && s !== " ")
            .join("; ") || MISSING_VALUE_DISPLAY
        );
      }
      const text = stringifyPrimitiveValue(values[0]);
      return !text || text === " " ? MISSING_VALUE_DISPLAY : text;
    }
  }
}

type UserLike = {
  firstname?: string | null;
  lastname?: string | null;
  email_address?: string | null;
};

function isUserLike(value: unknown): value is UserLike {
  return (
    !!value &&
    typeof value === "object" &&
    ("firstname" in value || "lastname" in value || "email_address" in value)
  );
}

function formatUserLike(user: UserLike): string {
  const fromParts = [user.firstname, user.lastname]
    .filter((part): part is string => Boolean(part && String(part).trim()))
    .map((part) => String(part).trim())
    .join(" ");
  if (fromParts) return fromParts;
  return user.email_address?.trim() || "";
}

function formatUserValue(value: AttributeValue): string {
  if (value.user) return formatUserLike(value.user);
  if (isUserLike(value.value)) return formatUserLike(value.value);
  return "";
}

function formatReferenceName(value: AttributeValue): string {
  if (value.object_ref?.name) return value.object_ref.name;
  if (value.product_ref?.name) return value.product_ref.name;
  const raw = value.value;
  if (raw && typeof raw === "object" && "name" in raw) {
    return String((raw as { name?: string }).name ?? "");
  }
  return typeof raw === "string" ? raw : "";
}

function formatComplexValue(raw: object): string {
  if (isUserLike(raw)) return formatUserLike(raw);
  if ("name" in raw && (raw as { name?: unknown }).name != null) {
    return String((raw as { name: unknown }).name);
  }
  if (
    "display_text" in raw &&
    (raw as { display_text?: unknown }).display_text != null
  ) {
    return String((raw as { display_text: unknown }).display_text);
  }
  // Never surface raw JSON / bare IDs as compare labels.
  return "";
}

function stringifyPrimitiveValue(value: AttributeValue | undefined): string {
  if (!value) return "";
  if (value.user) return formatUserLike(value.user);
  if (value.object_ref?.name) return value.object_ref.name;
  if (value.product_ref?.name) return value.product_ref.name;

  const raw = value.value;
  if (raw == null) return value.enum_variant?.variant ?? "";
  if (typeof raw === "boolean") return raw ? "On" : "Off";
  if (typeof raw === "object") return formatComplexValue(raw);
  const text = String(raw);
  return looksLikeHtml(text) ? stripHtmlTags(text) : text;
}

function getDocumentAssetId(value: AttributeValue): string {
  const raw = value.value;
  if (raw && typeof raw === "object" && "asset_id" in raw) {
    return String((raw as { asset_id?: string }).asset_id ?? "");
  }
  return "";
}

export function getCollectionRowLabel(
  collection: CollectionSchema,
  row: CollectionValueRowSchema,
  keyAttributeName?: string
): string {
  const keyColumn = keyAttributeName
    ? collection.columns.find(
        (c) => c.name.toLowerCase() === keyAttributeName.toLowerCase()
      )
    : collection.columns[0];
  if (!keyColumn) return row.row_id;
  const values = (row.values ?? []).filter(
    (v) => v.attribute_id === keyColumn.id
  );
  // Row headers only need a short label — use the first value, not every selection.
  const firstValue = values[0];
  if (!firstValue) return MISSING_VALUE_DISPLAY;
  return attributeValueToPlainText({
    ...keyColumn,
    values: [firstValue] as AttributeValue[],
  });
}

export type AlignedCollectionItem = {
  /** Unique key for map / React (stable even when display labels collide). */
  id: string;
  /** Display label from the Key Attribute (may be "--" for missing values). */
  rowKey: string;
  /** Per-product value_row for this aligned item (undefined if product lacks it). */
  rowsByProductId: Record<string, CollectionValueRowSchema | undefined>;
};

function collectionItemAlignmentKey(
  label: string,
  row: CollectionValueRowSchema
): string {
  const normalized = label.trim().toLowerCase();
  // Missing Key Attribute values all stringify to "--"; use row_id so they
  // don't collapse onto one map entry and overwrite each other.
  if (!normalized || normalized === MISSING_VALUE_DISPLAY.toLowerCase()) {
    return `__missing__:${row.row_id}`;
  }
  return normalized;
}

function compareAlignedCollectionItems(
  a: AlignedCollectionItem,
  b: AlignedCollectionItem
): number {
  const aMissing =
    !a.rowKey.trim() ||
    a.rowKey.trim() === MISSING_VALUE_DISPLAY ||
    a.id.startsWith("__missing__");
  const bMissing =
    !b.rowKey.trim() ||
    b.rowKey.trim() === MISSING_VALUE_DISPLAY ||
    b.id.startsWith("__missing__");
  if (aMissing && !bMissing) return 1;
  if (!aMissing && bMissing) return -1;
  return a.rowKey.localeCompare(b.rowKey, undefined, {
    sensitivity: "base",
    numeric: true,
  });
}

/**
 * Align collection items across products by the configured Key Attribute value.
 * Rows with a missing Key Attribute value sort to the bottom as separate items.
 */
export function getAlignedCollectionItems(
  products: PIMProduct[],
  collectionName: string,
  keyAttributeName?: string
): AlignedCollectionItem[] {
  const collections = products.map((p) => ({
    productId: p.id,
    collection: findCollectionByName(p, collectionName),
  }));

  const firstWithColumns = collections.find(
    (c) => c.collection?.columns?.length
  )?.collection;
  if (!firstWithColumns) return [];

  const items = new Map<string, AlignedCollectionItem>();

  for (const { productId, collection } of collections) {
    if (!collection?.value_rows) continue;
    for (const row of collection.value_rows) {
      const label = getCollectionRowLabel(collection, row, keyAttributeName);
      let mapKey = collectionItemAlignmentKey(label, row);
      let item = items.get(mapKey);

      // Same non-empty label twice on one product — keep both rows.
      if (item && item.rowsByProductId[productId] !== undefined) {
        mapKey = `${mapKey}::${row.row_id}`;
        item = undefined;
      }

      if (!item) {
        item = { id: mapKey, rowKey: label, rowsByProductId: {} };
        items.set(mapKey, item);
      }
      item.rowsByProductId[productId] = row;
    }
  }

  return Array.from(items.values()).sort(compareAlignedCollectionItems);
}

export function getCollectionAttributeForProduct(
  collection: CollectionSchema | undefined,
  row: CollectionValueRowSchema | undefined,
  column: AttributeSchema
): AttributeSchema | undefined {
  if (!collection || !row) return undefined;
  const matchingColumn = collection.columns.find(
    (c) => c.name.toLowerCase() === column.name.toLowerCase()
  );
  if (!matchingColumn || !isAttributeShownInCompare(matchingColumn)) {
    return undefined;
  }

  const values = (row.values ?? []).filter(
    (v) => v.attribute_id === matchingColumn.id
  );

  return {
    ...matchingColumn,
    values: values as AttributeValue[],
  };
}

export function getAlignedCollectionColumns(
  products: PIMProduct[],
  collectionName: string,
  keyAttributeName?: string
): AttributeSchema[] {
  const byName = new Map<string, AttributeSchema>();
  for (const product of products) {
    const collection = findCollectionByName(product, collectionName);
    for (const col of collection?.columns ?? []) {
      if (!byName.has(col.name.toLowerCase())) {
        byName.set(col.name.toLowerCase(), col);
      }
    }
  }
  const first = products
    .map((p) => findCollectionByName(p, collectionName))
    .find((c) => c?.columns?.length);
  if (!first) return Array.from(byName.values());

  const ordered: AttributeSchema[] = [];
  const used = new Set<string>();
  for (const col of first.columns) {
    const key = col.name.toLowerCase();
    const def = byName.get(key);
    if (def) {
      ordered.push(def);
      used.add(key);
    }
  }
  for (const [key, col] of byName) {
    if (!used.has(key)) ordered.push(col);
  }
  const visible = ordered.filter((colDef) =>
    products.some((product) => {
      const collection = findCollectionByName(product, collectionName);
      if (!isCollectionVisible(collection)) return false;
      const column = collection?.columns.find(
        (c) => c.name.toLowerCase() === colDef.name.toLowerCase()
      );
      return isAttributeShownInCompare(column);
    })
  );

  // Key Attribute (row identity) always appears first in the comparison.
  if (!keyAttributeName) return visible;
  const keyLower = keyAttributeName.toLowerCase();
  const keyColIndex = visible.findIndex(
    (col) => col.name.toLowerCase() === keyLower
  );
  if (keyColIndex <= 0) return visible;
  const keyCol = visible[keyColIndex];
  return [keyCol, ...visible.filter((_, i) => i !== keyColIndex)];
}

export function loadCompareMeta(): Record<string, CompareProductMeta> {
  try {
    const raw = sessionStorage.getItem(SESSION_COMPARE_META_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function saveCompareMeta(meta: Record<string, CompareProductMeta>) {
  sessionStorage.setItem(SESSION_COMPARE_META_KEY, JSON.stringify(meta));
}

export function pruneCompareMeta(selectedProductNumbers: string[]) {
  const meta = loadCompareMeta();
  const selected = new Set(selectedProductNumbers);
  const next: Record<string, CompareProductMeta> = {};
  for (const [key, value] of Object.entries(meta)) {
    if (selected.has(key)) next[key] = value;
  }
  saveCompareMeta(next);
  return next;
}

export function mergeCompareMetaFromTable(
  selectedProductNumbers: string[],
  tableRows: Array<{
    id: string;
    uuid?: string;
    template_id?: string | null;
    name: string;
  }>
) {
  const meta = loadCompareMeta();
  for (const row of tableRows) {
    if (!selectedProductNumbers.includes(row.id)) continue;
    const existing = meta[row.id];
    meta[row.id] = {
      product_number: row.id,
      uuid: row.uuid || existing?.uuid || "",
      template_id: row.template_id ?? existing?.template_id ?? null,
      name: row.name || existing?.name || row.id,
    };
  }
  const selected = new Set(selectedProductNumbers);
  const next: Record<string, CompareProductMeta> = {};
  for (const [key, value] of Object.entries(meta)) {
    if (selected.has(key)) next[key] = value;
  }
  saveCompareMeta(next);
  return next;
}
